import { ChatAnchor } from '@/types/chat';

const STORAGE_PREFIX = 'aleph:text-chat-draft:v1';
const STORAGE_VERSION = 1;

interface StoredDraftBase {
  version: typeof STORAGE_VERSION;
  text: string;
  updatedAt: string;
}

export interface StoredNewTextChatDraft extends StoredDraftBase {
  kind: 'new';
  anchor: ChatAnchor;
  excerpt: string | null;
}

export interface StoredSessionTextChatDraft extends StoredDraftBase {
  kind: 'session';
  sessionId: number;
}

function storageKey(userId: string, bookId: string, target: string): string {
  return `${STORAGE_PREFIX}:${encodeURIComponent(userId)}:${encodeURIComponent(bookId)}:${target}`;
}

function newDraftKey(userId: string, bookId: string): string {
  return storageKey(userId, bookId, 'new');
}

function sessionDraftKey(userId: string, bookId: string, sessionId: number): string {
  return storageKey(userId, bookId, `session:${sessionId}`);
}

function readJson(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Could not read text-chat draft from localStorage.', error);
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('Could not save text-chat draft to localStorage.', error);
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn('Could not remove text-chat draft from localStorage.', error);
  }
}

function isValidText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isValidAnchor(value: unknown): value is ChatAnchor {
  if (!value || typeof value !== 'object') return false;
  const anchor = value as Partial<ChatAnchor>;
  return typeof anchor.chapter_id === 'string' && typeof anchor.sentence_index === 'number';
}

export function readNewTextChatDraft(
  userId: string,
  bookId: string
): StoredNewTextChatDraft | null {
  const value = readJson(newDraftKey(userId, bookId));
  if (!value || typeof value !== 'object') return null;
  const draft = value as Partial<StoredNewTextChatDraft>;
  if (
    draft.version !== STORAGE_VERSION ||
    draft.kind !== 'new' ||
    !isValidText(draft.text) ||
    !isValidAnchor(draft.anchor)
  ) {
    return null;
  }
  return draft as StoredNewTextChatDraft;
}

export function writeNewTextChatDraft(
  userId: string,
  bookId: string,
  text: string,
  anchor: ChatAnchor,
  excerpt: string | null
): void {
  if (!text.trim()) {
    clearNewTextChatDraft(userId, bookId);
    return;
  }
  writeJson(newDraftKey(userId, bookId), {
    version: STORAGE_VERSION,
    kind: 'new',
    text,
    anchor,
    excerpt,
    updatedAt: new Date().toISOString(),
  } satisfies StoredNewTextChatDraft);
}

export function clearNewTextChatDraft(userId: string, bookId: string): void {
  remove(newDraftKey(userId, bookId));
}

export function readSessionTextChatDraft(
  userId: string,
  bookId: string,
  sessionId: number
): StoredSessionTextChatDraft | null {
  const value = readJson(sessionDraftKey(userId, bookId, sessionId));
  if (!value || typeof value !== 'object') return null;
  const draft = value as Partial<StoredSessionTextChatDraft>;
  if (
    draft.version !== STORAGE_VERSION ||
    draft.kind !== 'session' ||
    draft.sessionId !== sessionId ||
    !isValidText(draft.text)
  ) {
    return null;
  }
  return draft as StoredSessionTextChatDraft;
}

export function writeSessionTextChatDraft(
  userId: string,
  bookId: string,
  sessionId: number,
  text: string
): void {
  if (!text.trim()) {
    clearSessionTextChatDraft(userId, bookId, sessionId);
    return;
  }
  writeJson(sessionDraftKey(userId, bookId, sessionId), {
    version: STORAGE_VERSION,
    kind: 'session',
    sessionId,
    text,
    updatedAt: new Date().toISOString(),
  } satisfies StoredSessionTextChatDraft);
}

export function clearSessionTextChatDraft(
  userId: string,
  bookId: string,
  sessionId: number
): void {
  remove(sessionDraftKey(userId, bookId, sessionId));
}
